// Target list for personal tasks that are created without a list context —
// the quick-add under „Alle Aufgaben" in /lists.
//
// Resolution order (Story: jede neue Aufgabe landet in einer Liste):
//   1. the list the user configured in Einstellungen (user_preferences
//      .default_list_id) — only if it is still there and writable,
//   2. otherwise the user's first personal list (sidebar order),
//   3. otherwise null → the caller shows a hint instead of creating a
//      list-less task.
//
// Seit „Ohne Liste" weg ist (Server-Migration 0092) gibt es keinen Weg mehr,
// eine Aufgabe ohne Liste zu erzeugen: nennt der Client keine, wählt der Server
// dieselbe Reihenfolge und legt notfalls die Liste „Persönlich" an. Diese
// Auflösung hier bleibt trotzdem die Vorauswahl in der Oberfläche — der Nutzer
// soll VOR dem Anlegen sehen, wo die Aufgabe landet.

import type { TaskList } from "@juuplan/shared";

/** True for a personal (non-space) list. */
function isPersonal(list: TaskList): boolean {
  return list.scope !== "space";
}

/** True when the user may create tasks in this list. */
function isWritable(list: TaskList): boolean {
  if (isPersonal(list)) return true;
  return list.role === "owner" || list.role === "editor";
}

/** Personal lists in sidebar order (the API already sorts by sort_order, id). */
export function personalLists(lists: TaskList[]): TaskList[] {
  return lists.filter(isPersonal);
}

/** Every list the user may create tasks in — personal plus shared as editor. */
export function writableLists(lists: TaskList[]): TaskList[] {
  return lists.filter(isWritable);
}

/**
 * The list a context-less new task belongs in, or null when the user has no
 * usable list at all. `configuredId` is user_preferences.default_list_id.
 */
export function resolveTargetList(
  lists: TaskList[],
  configuredId: number | null
): TaskList | null {
  if (configuredId != null) {
    const configured = lists.find((l) => l.id === configuredId);
    if (configured && isWritable(configured)) return configured;
  }
  return personalLists(lists)[0] ?? null;
}
